import * as React from 'react';
import { Alert } from '@components/base/feedback';
import { Button } from '@components/base/actions';
import { Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

type AlertVariant = 'info' | 'success' | 'warning' | 'error';

function AlertShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  // State for dismissible alerts
  const [dismissedAlerts, setDismissedAlerts] = React.useState<Record<string, boolean>>({});

  const resetDismissed = () => {
    setDismissedAlerts({});
  };

  const dismissAlert = (id: string) => {
    setDismissedAlerts((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="alert">
          <Alert
            variant={playgroundProps.variant as AlertVariant}
            dismissible={playgroundProps.dismissible as boolean}
            onDismiss={() => {}}
          >
            This is an alert message configured from the Props Playground.
          </Alert>
      </ShowcasePreviewSection>

      {/* Variants */}
      <ShowcaseDemoSection
        title="Variants"
        description="Four semantic variants for different message types"
       
      >
          <VStack gap={4}>
            <Alert variant="info">
              Your session will expire in 10 minutes. Save your work to avoid losing changes.
            </Alert>
            <Alert variant="success">
              Policy POL-2024-001842 has been successfully created and is now active.
            </Alert>
            <Alert variant="warning">
              This claim requires additional documentation before processing can continue.
            </Alert>
            <Alert variant="error">
              Unable to submit claim. Please check your network connection and try again.
            </Alert>
          </VStack>
      </ShowcaseDemoSection>

      {/* With Title */}
      <ShowcaseDemoSection
        title="With Title"
        description="Add a prominent title above the message content"
       
      >
          <VStack gap={4}>
            <Alert variant="info" title="Session Information">
              Your current session has been active for 2 hours. Remember to save your work periodically.
            </Alert>
            <Alert variant="success" title="Claim Approved">
              Claim CLM-2024-005678 has been approved for $12,500. The policyholder will be notified via email.
            </Alert>
            <Alert variant="warning" title="Pending Review">
              3 claims are awaiting your review. Please complete your assessments by end of business today.
            </Alert>
            <Alert variant="error" title="Validation Failed">
              The policy effective date cannot be in the past. Please select a valid date.
            </Alert>
          </VStack>
      </ShowcaseDemoSection>

      {/* Dismissible Alerts */}
      <ShowcaseDemoSection
        title="Dismissible Alerts"
        description="Allow users to dismiss alerts with a close button"
       
      >
          <VStack gap={4}>
            {!dismissedAlerts['dismiss-1'] && (
              <Alert
                variant="info"
                title="New Feature Available"
                dismissible
                onDismiss={() => dismissAlert('dismiss-1')}
              >
                You can now export claim reports directly to PDF. Try it from the Reports menu.
              </Alert>
            )}
            {!dismissedAlerts['dismiss-2'] && (
              <Alert
                variant="success"
                title="Document Uploaded"
                dismissible
                onDismiss={() => dismissAlert('dismiss-2')}
              >
                Medical records have been successfully uploaded to claim CLM-2024-003456.
              </Alert>
            )}
            {!dismissedAlerts['dismiss-3'] && (
              <Alert
                variant="warning"
                dismissible
                onDismiss={() => dismissAlert('dismiss-3')}
              >
                Your password will expire in 7 days. Consider updating it soon.
              </Alert>
            )}
            {Object.values(dismissedAlerts).some(Boolean) && (
              <Button variant="secondary" onClick={resetDismissed}>
                Reset Dismissed Alerts
              </Button>
            )}
          </VStack>
      </ShowcaseDemoSection>

      {/* Real-world Examples */}
      <ShowcaseDemoSection
        title="Real-world Examples"
        description="Common alert patterns in insurance applications"
       
      >
          <VStack gap={4}>
            {/* Form validation errors */}
            <Alert variant="error" title="Please correct the following errors">
              <VStack gap={1} className="mt-2">
                <Text size="sm">- Policy number is required</Text>
                <Text size="sm">- Coverage amount must be greater than $0</Text>
                <Text size="sm">- Effective date must be within 90 days</Text>
              </VStack>
            </Alert>

            {/* Success confirmation */}
            <Alert variant="success" title="Claim Submitted Successfully">
              <VStack gap={2} className="mt-2">
                <Text size="sm">
                  Claim reference: <Text as="span" weight="semibold">CLM-2024-007890</Text>
                </Text>
                <Text size="sm">
                  A confirmation email has been sent to the policyholder at j.smith@email.com
                </Text>
              </VStack>
            </Alert>

            {/* Policy renewal warning */}
            <Alert variant="warning" title="Policy Renewal Due">
              <VStack gap={2} className="mt-2">
                <Text size="sm">
                  Policy POL-2024-001234 expires on March 15, 2024. Contact the policyholder
                  to discuss renewal options.
                </Text>
                <HStack gap={2} className="mt-2">
                  <Button size="sm">View Policy</Button>
                  <Button size="sm" variant="secondary">Schedule Call</Button>
                </HStack>
              </VStack>
            </Alert>

            {/* System information */}
            <Alert variant="info" title="Scheduled Maintenance">
              The system will be unavailable on Saturday, March 2nd from 2:00 AM to 6:00 AM EST
              for scheduled maintenance. Please plan accordingly.
            </Alert>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const AlertShowcase: ShowcaseDefinition = {
  id: 'alert',
  name: 'Alert',
  category: 'feedback',
  description: 'Inline notification for important messages',
  propsDefinition: [
    {
      name: 'variant',
      type: 'select',
      options: ['info', 'success', 'warning', 'error'],
      defaultValue: 'info',
      description: 'The visual style variant of the alert',
    },
    {
      name: 'dismissible',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the alert can be dismissed with a close button',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="alert"',
      'aria-live="assertive" (implicit)',
    ],
    keyboardNav: [
      'Tab: Focus dismiss button',
      'Enter/Space: Dismiss alert (when focused)',
    ],
    notes: [
      'Uses semantic role="alert" for screen reader announcements',
      'Dismissible alerts include an accessible close button',
      'Color is not the only indicator - icons provide additional context',
    ],
  },
  component: AlertShowcaseComponent,
};
